import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import {
  FiMessageCircle,
  FiPhone,
  FiRefreshCw,
  FiSearch,
  FiUser,
} from "react-icons/fi";
import PageHeader from "../../components/common/PageHeader";
import { fetchWhatsappMessages } from "../../service/apiService";
import "./MessagesPage.css";

function formatMessageTime(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(Number(value) * 1000);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatGroupLabel(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(Number(value) * 1000);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
  }).format(date);
}

function getInitials(name, phone) {
  const source = String(name || phone || "").trim();
  if (!source) {
    return "?";
  }

  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function threadPreview(thread) {
  const message = String(thread?.last_message || "").trim();
  if (message) {
    return message;
  }

  const type = String(thread?.last_message_type || "").trim();
  if (type) {
    return type.charAt(0).toUpperCase() + type.slice(1);
  }

  return "No preview available";
}

export default function MessagesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [selectedWaId, setSelectedWaId] = useState(searchParams.get("wa_id") || "");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  useEffect(() => {
    const waIdFromUrl = searchParams.get("wa_id") || "";
    setSelectedWaId(waIdFromUrl);
  }, [searchParams]);

  const loadMessages = async ({ waId = selectedWaId, silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError("");

    try {
      const res = await fetchWhatsappMessages({ wa_id: waId, limit: 5000 });

      if (res?.status === "success") {
        setThreads(res.threads || []);
        setMessages(res.messages || []);
        setSelectedThread(res.selected_thread || null);
        const nextWaId = res.meta?.wa_id || "";

        if (nextWaId && nextWaId !== selectedWaId) {
          setSelectedWaId(nextWaId);
          setSearchParams({ wa_id: nextWaId }, { replace: true });
        } else if (!nextWaId) {
          setSelectedWaId("");
          setSearchParams({}, { replace: true });
        }

        setLastLoadedAt(new Date());
      } else {
        setError(res?.message || "Failed to load messages");
      }
    } catch (loadError) {
      console.error("Messages load error:", loadError);
      setError("Server not reachable");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadMessages({ waId: selectedWaId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWaId]);

  const visibleThreads = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return threads;
    }

    return threads.filter((thread) => {
      return (
        String(thread.wa_id || "").toLowerCase().includes(query) ||
        String(thread.sender_name || "").toLowerCase().includes(query) ||
        String(thread.last_message || "").toLowerCase().includes(query) ||
        String(thread.last_message_type || "").toLowerCase().includes(query)
      );
    });
  }, [threads, search]);

  const handleSelectThread = (thread) => {
    if (!thread?.wa_id) {
      return;
    }

    setSelectedWaId(thread.wa_id);
    setSearchParams({ wa_id: thread.wa_id }, { replace: false });
  };

  const currentDateLabel = useMemo(() => {
    if (!messages.length) {
      return "";
    }

    return formatGroupLabel(messages[0]?.whatsapp_timestamp);
  }, [messages]);

  return (
    <Container fluid className="admin-page messages-page">
      <PageHeader
        title="Messages"
        subtitle="Browse WhatsApp replies pulled from webhook logs"
        action={
          <div className="d-flex align-items-center gap-2">
            {lastLoadedAt ? (
              <span className="messages-updated">
                Updated {lastLoadedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            ) : null}
            <Button
              variant="outline-success"
              className="messages-refresh-btn"
              onClick={() => loadMessages({ waId: selectedWaId, silent: true })}
              disabled={loading || refreshing}
            >
              <FiRefreshCw className={refreshing ? "spin" : ""} />
              Refresh
            </Button>
          </div>
        }
      />

      {error ? (
        <Card className="border-0 shadow-sm mb-3">
          <Card.Body>
            <div className="text-danger">{error}</div>
          </Card.Body>
        </Card>
      ) : null}

      <Row className="g-3 messages-shell">
        <Col xl={4} lg={5}>
          <Card className="shadow-sm border-0 messages-panel messages-thread-panel">
            <Card.Body className="p-0">
              <div className="messages-thread-header">
                <div>
                  <div className="messages-panel-title">Chats</div>
                  <div className="messages-panel-subtitle">
                    {visibleThreads.length} conversation{visibleThreads.length === 1 ? "" : "s"}
                  </div>
                </div>
                <Badge bg="light" text="dark" className="messages-badge">
                  WhatsApp
                </Badge>
              </div>

              <div className="messages-search-wrap">
                <FiSearch className="messages-search-icon" />
                <Form.Control
                  className="messages-search"
                  placeholder="Search by name, phone, or text"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>

              <div className="messages-thread-list">
                {loading ? (
                  <div className="messages-loader">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : visibleThreads.length === 0 ? (
                  <div className="messages-empty">
                    <FiMessageCircle size={28} />
                    <div className="messages-empty-title">No chats found</div>
                    <div className="messages-empty-subtitle">
                      Try a different name, phone number, or keyword.
                    </div>
                  </div>
                ) : (
                  visibleThreads.map((thread) => {
                    const active = thread.wa_id === selectedThread?.wa_id;

                    return (
                      <button
                        key={thread.wa_id}
                        type="button"
                        className={`message-thread-item ${active ? "active" : ""}`}
                        onClick={() => handleSelectThread(thread)}
                      >
                        <div className="message-avatar">{getInitials(thread.sender_name, thread.wa_id)}</div>

                        <div className="message-thread-content">
                          <div className="message-thread-top">
                            <div className="message-thread-name">{thread.sender_name || thread.wa_id}</div>
                            <div className="message-thread-time">
                              {formatMessageTime(thread.last_message_at)}
                            </div>
                          </div>
                          <div className="message-thread-meta">
                            <FiPhone size={12} />
                            <span>{thread.wa_id}</span>
                          </div>
                          <div className="message-thread-preview">{threadPreview(thread)}</div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={8} lg={7}>
          <Card className="shadow-sm border-0 messages-panel messages-chat-panel">
            <Card.Body className="p-0 messages-chat-shell">
              <div className="messages-chat-header">
                <div className="d-flex align-items-center gap-3">
                  <div className="message-avatar large">
                    {getInitials(selectedThread?.sender_name, selectedThread?.wa_id)}
                  </div>

                  <div>
                    <div className="messages-chat-name">{selectedThread?.sender_name || "Select a chat"}</div>
                    <div className="messages-chat-subtitle">
                      <FiUser className="me-1" />
                      {selectedThread?.wa_id || "Choose a thread from the left panel"}
                    </div>
                  </div>
                </div>

                <div className="messages-chat-status">
                  <Badge bg={selectedThread ? "success" : "secondary"}>
                    {selectedThread ? "Active" : "No chat selected"}
                  </Badge>
                </div>
              </div>

              <div className="messages-chat-body">
                {messages.length === 0 ? (
                  <div className="messages-empty chat-empty">
                    <FiMessageCircle size={30} />
                    <div className="messages-empty-title">No messages yet</div>
                    <div className="messages-empty-subtitle">
                      Incoming WhatsApp replies will appear here once the webhook saves them.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="chat-date-pill">{currentDateLabel}</div>
                    <div className="chat-message-stack">
                      {messages.map((message) => (
                        <div key={message.id} className="chat-message-row incoming">
                          <div className="chat-message-bubble">
                            <div className="chat-message-text">{message.message_text || message.message_type}</div>
                            <div className="chat-message-footer">
                              <span>{formatMessageTime(message.whatsapp_timestamp)}</span>
                              <Badge bg="light" text="dark" className="message-type-badge">
                                {message.message_type || "text"}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="messages-reply-box">
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Reply API will be added here soon"
                  disabled
                />
                <div className="messages-reply-hint">
                  Reply composer is ready for the next API step.
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
